import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BoltIcon from '@mui/icons-material/Bolt';
import SecurityIcon from '@mui/icons-material/Security';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import { CardComponent } from '@chargebee/chargebee-js-react-wrapper';
import { toast } from 'react-toastify';
import ewSmallLogo from '../../../assets/images/ew-small-logo.webp';
import chargebeeService from '../../../services/chargebee.service';
import AuthService from '../../../services/auth.service';
import { decodeMetadataToken } from '../../../utils/token.utils';
import { STORAGE_KEYS } from '../../../utils/storage.util';

const TRIAL_CHARGE_ITEM_PRICE_ID = 'Trial-Charge-USD';

const getTrialStartDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const PLAN_MAP: Record<string, { name: string; priceMonthly: number }> = {
  'Live-Webinars-USD-Monthly-50': { name: 'Launch', priceMonthly: 44 },
  'All-In-One-USD-Monthly-200': { name: 'Pro', priceMonthly: 116 },
  'All-In-One-USD-Monthly-500': { name: 'Growth', priceMonthly: 144 },
};

const DEFAULT_PLAN_ID = 'All-In-One-USD-Monthly-200';

function parsePlanIdToBaseAndUnits(planId: string): { basePlanId: string; units: number } | null {
  const match = planId.match(/^(.+)-(\d+)$/);
  if (!match) return null;
  return { basePlanId: match[1], units: parseInt(match[2], 10) };
}

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedPlanId = searchParams.get('plan') || DEFAULT_PLAN_ID;
  const plan = PLAN_MAP[selectedPlanId] || PLAN_MAP[DEFAULT_PLAN_ID];

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cardRef = useRef<any>(null);

  const customerId = useMemo(() => {
    const metadata = decodeMetadataToken();
    return metadata?.companies?.[0]?.id ?? null;
  }, []);

  const priceToday = 1;
  const canSubmit =
    Boolean(customerId) && termsAccepted && isCardValid && !isSubmitting;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit || !cardRef.current) return;

      if (!customerId) {
        toast.error('Please log in to add payment.');
        navigate('/try-ai-flow/login', { replace: true });
        return;
      }

      setIsSubmitting(true);
      setCardError(null);
      try {
        console.log('[Payment] Selected plan from params:', selectedPlanId);
        console.log('[Payment] Step 1: Tokenizing card...');
        const result = await cardRef.current.tokenize();
        if (result.error) {
          setCardError(result.error.message || 'Card tokenization failed');
          return;
        }
        const cardToken = result.token;
        if (!cardToken) {
          setCardError('Invalid card. Please try again.');
          return;
        }
        console.log('[Payment] Step 1: Card tokenized successfully');

        console.log('[Payment] Step 2: Adding payment source...');
        await chargebeeService.addPaymentSource(customerId, cardToken);
        console.log('[Payment] Step 2: Payment source added successfully');

        console.log('[Payment] Step 3: Quick charge ($1)...');
        await chargebeeService.quickCharge(
          customerId,
          TRIAL_CHARGE_ITEM_PRICE_ID,
          1
        );
        console.log('[Payment] Step 3: Quick charge successful');

        console.log('[Payment] Step 4: Refreshing token to get latest subscription...');
        await AuthService.refreshToken();
        const metadata = decodeMetadataToken();
        const subscriptionId = metadata?.companies?.[0]?.subscription?.id;
        console.log('[Payment] Step 4: subscriptionId =', subscriptionId);

        if (subscriptionId) {
          const parsed = parsePlanIdToBaseAndUnits(selectedPlanId);
          if (parsed) {
            console.log('[Payment] Step 5: Changing plan to', parsed.basePlanId, 'units:', parsed.units);
            await chargebeeService.changePlan(
              subscriptionId,
              parsed.basePlanId,
              parsed.units
            );
            console.log('[Payment] Step 5: Change plan successful');
          } else {
            console.warn('[Payment] Step 5: Could not parse plan id for changePlan:', selectedPlanId);
          }
        } else {
          console.warn('[Payment] Step 5: No subscriptionId in metadata, skipping changePlan');
        }

        toast.success(`Your $${priceToday} trial has been activated.`);
        const base = (process.env.REACT_APP_EASYWEBINAR_V2_URL ?? '').replace(/\/$/, '');
        const v2Url = `${base}/ai-webinar/try-ai-flow/registration-link`;
        const state = localStorage.getItem(STORAGE_KEYS.STATE);
        const qs = state ? `?state=${encodeURIComponent(state)}` : '';
        window.location.replace(`${v2Url}${qs}`);
      } catch (err: any) {
        const message =
          err?.message || 'Payment failed. Please check your card and try again.';
        setCardError(message);
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [canSubmit, customerId, navigate, priceToday, selectedPlanId]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        py: 4,
        background:
          'linear-gradient(150deg, #DEEEF1 2.21%, #FFF 50.41%)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 520,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Link
          to="/try-ai-flow"
          style={{
            flexShrink: 0,
            marginBottom: 24,
            display: 'block',
            borderRadius: 8,
          }}
        >
          <img
            src={ewSmallLogo}
            alt="EasyWebinar"
            style={{ display: 'block', height: 40 }}
          />
        </Link>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            textAlign: 'center',
            mb: 0.5,
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
          }}
        >
          Start Your 7-Day Trial
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          <BoltIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#4E61F6' }} />
          {plan.name} Plan – Add your payment details to publish.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          {/* Billing Summary */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(78, 97, 246, 0.2)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              p: 2.5,
              mb: 2,
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                pb: 1.5,
                borderBottom: '1px solid rgba(78, 97, 246, 0.15)',
              }}
            >
              <SecurityIcon sx={{ color: '#4E61F6', fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Billing Summary
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {[
                `$${priceToday} charged today to activate your trial`,
                '7 days of full access',
                `Cancel anytime before ${getTrialStartDate()} to avoid future charges`,
                `On ${getTrialStartDate()}, your selected plan renews at $${plan.priceMonthly}/month`,
              ].map((text, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <CheckIcon sx={{ fontSize: 18, color: '#4E61F6', mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {i === 2 ? (
                      <>
                        Cancel anytime before <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{getTrialStartDate()}</Box> to avoid future charges
                      </>
                    ) : i === 3 ? (
                      <>
                        On <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{getTrialStartDate()}</Box>, your selected plan renews at{' '}
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>${plan.priceMonthly}/month</Box>
                      </>
                    ) : (
                      text
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Payment Details */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(78, 97, 246, 0.25)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              p: 2.5,
              mb: 2,
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                pb: 1.5,
                borderBottom: '1px solid rgba(78, 97, 246, 0.2)',
              }}
            >
              <CreditCardIcon sx={{ color: '#4E61F6', fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Payment Details
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                '& .CbHosted': {
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  padding: '14px 16px',
                  '&:hover, &--focus': {
                    borderColor: '#666666',
                  },
                },
              }}
            >
              <CardComponent
                ref={cardRef}
                currency="USD"
                showTestCards={true}
                styles={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  base: {
                    padding: '14px 16px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    color: '#333333',
                    borderRadius: '8px',
                    '::placeholder': {
                      color: '#667085',
                    },
                  },
                  invalid: {
                    color: '#ff0000',
                  },
                }}
                onChange={(status: any) => {
                  if (status.error) {
                    setCardError(status.error.message);
                    setIsCardValid(false);
                  } else if (!status.complete) {
                    setCardError(null);
                    setIsCardValid(false);
                  } else {
                    setCardError(null);
                    setIsCardValid(true);
                  }
                }}
                onReady={() => {
                  setIsCardValid(false);
                }}
              />
            </Box>
            {cardError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {cardError}
              </Typography>
            )}
          </Paper>

          {/* Terms checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                size="small"
                sx={{
                  color: '#4E61F6',
                  '&.Mui-checked': { color: '#4E61F6' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                I understand that I will be charged <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>${priceToday} today</Box> and{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>${plan.priceMonthly}/month</Box> starting on{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{getTrialStartDate()}</Box> unless I cancel before then.
              </Typography>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          {!customerId && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
                textAlign: 'center',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              Please{' '}
              <Typography
                component={Link}
                to="/try-ai-flow/login"
                variant="body2"
                sx={{ color: '#4E61F6', fontWeight: 600, textDecoration: 'underline', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                log in
              </Typography>
              {' '}to add your payment and start the trial.
            </Typography>
          )}

          {/* CTA */}
          <Button
            variant="contained"
            type="submit"
            disabled={!canSubmit}
            fullWidth
            startIcon={<BoltIcon />}
            sx={{
              py: { xs: 0.875, sm: 1 },
              borderRadius: '14.225px',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              bgcolor: '#4E61F6',
              color: '#fff',
              '&:hover': {
                bgcolor: '#3D4FD9',
                boxShadow: '0 4px 14px rgba(78, 97, 246, 0.35)',
                color: '#fff !important',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.600',
              },
            }}
          > 
            {isSubmitting ? 'Processing…' : `Start $${priceToday} Trial & Publish`}
          </Button>

          {/* Footer disclaimer */}
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 2, lineHeight: 1.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            By continuing, you agree to our{' '}
            <Typography component="a" href="https://easywebinar.com/terms-conditions/" variant="caption" sx={{ color: '#4E61F6', textDecoration: 'underline' }}>
              Terms of Service
            </Typography>
            {' '}and{' '}
            <Typography component="a" href="/subscription-policy" variant="caption" sx={{ color: '#4E61F6', textDecoration: 'underline' }}>
              Subscription Policy
            </Typography>
            . Your subscription will automatically renew unless canceled before the renewal date.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 1.5,
            }}
          >
            <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Secured with 256-bit SSL encryption
            </Typography>
          </Box>
        </Box>

        <Typography
          component={Link}
          to="/try-ai-flow/pricing"
          variant="body2"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 3,
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': { color: '#4b5563', textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          Change plan
        </Typography>
      </Box>
    </Box>
  );
}
